/**
 * Full-width teal tab header for tabbed admin add/edit forms. Renders the row
 * that used to be hand-inlined in each form (`<Box><Tabs variant="fullWidth">`).
 * Styling comes from `adminFormChrome.styles.ts` so the colour lives in one
 * place. A tab flagged `hasError` is tinted red so a failed submit points the
 * user at the offending tab even when they're looking at another one.
 */
import React from 'react'
import { Badge, Box, Tab, Tabs } from '@mui/material'
import { formHeaderBoxSx, formHeaderTabsSx } from './adminFormChrome.styles'

export interface AdminFormTab {
  label: string
  icon?: React.ReactElement
  /** Show a small red dot on the tab - set when it contains a validation error. */
  hasError?: boolean
  /**
   * Show a small count pill after the label (e.g. how many items the tab holds).
   * `0` renders a muted pill; omit entirely to show no pill.
   */
  count?: number
}

/** Label + optional trailing count pill (teal when populated, muted at zero). */
const TabLabel: React.FC<{ label: string; count?: number }> = ({ label, count }) => {
  if (count === undefined) return <>{label}</>
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      {label}
      <Box
        component="span"
        sx={{
          minWidth: 18,
          height: 18,
          px: 0.5,
          borderRadius: '9px',
          fontSize: '0.7rem',
          fontWeight: 700,
          lineHeight: '18px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: count > 0 ? 'rgba(0,127,152,0.12)' : 'action.hover',
          color: count > 0 ? 'primary.main' : 'text.secondary',
        }}
      >
        {count}
      </Box>
    </Box>
  )
}

export interface AdminFormTabHeaderProps {
  tabs: AdminFormTab[]
  value: number
  onChange: (event: React.SyntheticEvent, next: number) => void
}

const AdminFormTabHeader: React.FC<AdminFormTabHeaderProps> = ({ tabs, value, onChange }) => (
  <Box sx={formHeaderBoxSx}>
    <Tabs value={value} onChange={onChange} variant="fullWidth" sx={formHeaderTabsSx}>
      {tabs.map((tab, index) => (
        <Tab
          key={tab.label}
          icon={tab.icon}
          iconPosition="start"
          label={
            tab.hasError ? (
              <Badge
                color="error"
                variant="dot"
                sx={{ '& .MuiBadge-badge': { right: -7, top: 3 } }}
              >
                <TabLabel label={tab.label} count={tab.count} />
              </Badge>
            ) : (
              <TabLabel label={tab.label} count={tab.count} />
            )
          }
          aria-controls={`form-tabpanel-${index}`}
        />
      ))}
    </Tabs>
  </Box>
)

export default AdminFormTabHeader
